import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import DecisionPanel from "../components/DecisionPanel";

export default function DecisionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [decision, setDecision] = useState(null);
  const [subjectsById, setSubjectsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/decisions/${id}`);
        if (cancelled) return;
        setDecision(res.data);
        // Fetch subject documents in parallel for friendly names in the matrix
        const ids = res.data.subject_doc_ids || [];
        const docs = await Promise.all(
          ids.map((did) =>
            api.get(`/documents/${did}`).then((r) => r.data).catch(() => null)
          )
        );
        if (cancelled) return;
        const map = {};
        for (const d of docs) {
          if (d?.id) map[d.id] = d;
        }
        setSubjectsById(map);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || "Decision not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="grid place-items-center py-24" data-testid="decision-loading">
        <Loader2 size={20} className="animate-spin text-zinc-400" />
      </div>
    );
  }
  if (error || !decision) {
    return (
      <div className="text-center py-20" data-testid="decision-error">
        <p className="text-zinc-700 mb-3">{error || "Decision not found"}</p>
        <Link to="/dashboard" className="text-zinc-950 underline">Back to dashboard</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-up" data-testid="decision-detail-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="decision-back-button"
        className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
      >
        <ArrowLeft size={14} /> Back
      </button>
      <DecisionPanel decision={decision} subjectsById={subjectsById} />
      {decision.subject_doc_ids?.length > 0 && (
        <div className="text-xs text-zinc-500" data-testid="decision-subject-links">
          Subjects:{" "}
          {decision.subject_doc_ids.map((did, i) => (
            <Link key={did} to={`/analysis/${did}`} className="underline mr-2" data-testid={`decision-subject-link-${i}`}>
              {subjectsById[did]?.filename || did.slice(0, 8)}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
